/** @jsxImportSource @emotion/react */
import { withMyTheme } from "../../theme/theme"
import { alpha, Box, css, Paper, Theme, Typography } from "@mui/material"
import { Fullscreen } from "../../components/Fullscreen"
import { useTranslation } from "react-i18next"
import { MyText } from "../../components/text/MyText"
import { mobileCss } from "../../util/isMobile"
import { SocialMediaIcons } from "../footer/SocialMediaIcons"
import { Email, Favorite, Phone } from "@mui/icons-material"
import { ImageResource } from "../../resources/ImageResource"
import { ContactForm } from "./ContactForm"
import { QuestionnaireCard } from "./QuestionnaireCard"
import { PHONE_NUMBER } from "../../components/phoneNumber/PhoneNumber"

export const CONTACT_ID = 'contact'
export const EMAIL = 'the.weddie.site@gmail.com'

const ContactStyle = withMyTheme((theme) => css`
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 5rem 0;
    background: url(${ImageResource.contactBackground}) no-repeat center center;
    background-size: cover;
    position: relative;
    
    &::before {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background: linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.7)} 0%, ${alpha(theme.palette.secondary.main, 0.7)} 100%);
        z-index: 1;
    }
`)

const ContactCardStyle = withMyTheme((theme) => css`
    width: 70vw;
    display: flex;
    flex-direction: column;
    border-radius: 24px;
    box-shadow: 0 20px 80px rgba(0, 0, 0, 0.25);
    overflow-y: auto;
    background-color: white;
    position: relative;
    z-index: 2;
    
    ${mobileCss(`
        width: 92vw;
    `)}
`)

const ContactContentStyle = withMyTheme((theme) => css`
    display: flex;
    flex-direction: column;
    position: relative;
    overflow: hidden;
    padding: 5vh;
    
    &::before {
        content: '';
        position: absolute;
        top: -10%;
        right: -20%;
        width: 300px;
        height: 300px;
        border-radius: 50%;
        background: radial-gradient(circle, ${alpha(theme.palette.primary.light, 0.2)} 0%, transparent 70%);
        z-index: 0;
    }
    
    &::after {
        content: '';
        position: absolute;
        bottom: -10%;
        left: -10%;
        width: 250px;
        height: 250px;
        border-radius: 50%;
        background: radial-gradient(circle, ${alpha(theme.palette.secondary.light, 0.2)} 0%, transparent 70%);
        z-index: 0;
    }
`)

const ContactRowStyle = withMyTheme((theme) => css`
    display: flex;
    flex-direction: row;
    width: 100%;
    position: relative;
    align-items: center;
    z-index: 1;
    
    ${mobileCss(`
        flex-direction: column;
    `)}
`)

const ContactColumnStyle = withMyTheme((theme) => css`
    flex: 1;
    display: flex;
    flex-direction: column;
    padding: 0 1.5rem;
    
    &:first-of-type {
        border-right: 1px solid ${alpha(theme.palette.primary.main, 0.1)};
    }
    
    ${mobileCss(`
        padding: 0;
        
        &:first-of-type {
            border-right: none;
            border-bottom: 1px solid ${alpha(theme.palette.primary.main, 0.1)};
            padding-bottom: 2rem;
            margin-bottom: 2rem;
        }
    `)}
`)

const ContactTitleStyle = withMyTheme((theme) => css`
    font-size: 2.5rem;
    font-weight: 700;
    margin-bottom: 1.5rem;
    color: ${theme.palette.primary.main};
    position: relative;
    text-align: center;
    
    ${mobileCss(`
        font-size: 2rem;
    `)}
`)

const ContactDescriptionStyle = withMyTheme((theme) => css`
    font-size: 1.3rem;
    line-height: 1.6;
    margin-bottom: 2rem;
    position: relative;
    z-index: 1;
    text-align: center;
    
    ${mobileCss(`
        font-size: 1.1rem;
        text-align: center;
    `)} 
`)

const ContactInfoAndSocialsStyle = withMyTheme((theme) => css`
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    gap: 3vh;
    margin: 5vh 0 0 0;
    position: relative;
    z-index: 1;
    
    ${mobileCss(`
        flex-direction: column;
        gap: 1.5rem;
    `)}
`)

const ContactInfoStyle = withMyTheme((theme) => css`
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 1.5rem;
    
    ${mobileCss(`
        flex-direction: column;
        gap: 1rem;
    `)}
`)

const InfoItemStyle = withMyTheme((theme) => css`
    display: flex;
    align-items: center;
    
    svg {
        color: ${theme.palette.primary.main};
        margin-right: 0.5rem;
        font-size: 1.5rem;
    }
`)

const SocialIconsContainerStyle = withMyTheme((theme) => css`
    display: flex;
    flex-direction: column;
    align-items: center;
`)

const SocialTextStyle = withMyTheme((theme) => css`
    font-size: 1.1rem;
    display: flex;
    align-items: center;
    
    svg {
        color: ${theme.palette.primary.main};
        margin-right: 0.5rem;
    }
`)

const AccentTextStyle = withMyTheme((theme) => css`
    color: ${theme.palette.primary.main};
    font-weight: 600;
`)

const ContactDescriptionContainerStyle = withMyTheme((theme) => css`
    display: flex;
    justify-content: center;
    align-items: center;
    margin-bottom: 2.5rem;
    position: relative;
    padding-bottom: 1.5rem;
    
    &::after {
        content: '';
        position: absolute;
        bottom: 0;
        left: 50%;
        transform: translateX(-50%);
        width: 60%;
        height: 3px;
        background: linear-gradient(90deg, transparent, ${theme.palette.primary.main}, transparent);
        z-index: 1;
    }
`)

export const Contact = () => {
    const { t } = useTranslation();

    return (
        <section id={CONTACT_ID} css={ContactStyle}>
            <Paper css={ContactCardStyle} elevation={6}>
                <div css={ContactContentStyle}>
                    {/* <TitleAndDescription /> */}
                    <div css={ContactRowStyle}>
                        <div css={ContactColumnStyle}>
                            <ContactForm />
                        </div>

                        <div css={ContactColumnStyle}>
                            <QuestionnaireCard />
                        </div>
                    </div>

                    <div css={ContactInfoAndSocialsStyle}>
                        <div css={ContactInfoStyle}>
                            <div css={InfoItemStyle}>
                                <Email />
                                <MyText text={EMAIL} />
                            </div>
                            <div css={InfoItemStyle}>
                                <Phone />
                                <MyText text={PHONE_NUMBER} />
                            </div>
                        </div>

                        <div css={SocialIconsContainerStyle}>
                            <SocialMediaIcons brownIcons={true} />
                        </div>
                    </div>

                </div>
            </Paper>
        </section>
    );
}

const TitleAndDescription = () => {
    const { t } = useTranslation();

    return <>
        <Typography css={ContactTitleStyle}>
            {t('contact.pageTitle')}
        </Typography>

        <div css={ContactDescriptionContainerStyle}>
            <Typography css={ContactDescriptionStyle}
                dangerouslySetInnerHTML={{
                    __html: t('contact.detailedDescription').replace(
                        /<accent>(.*?)<\/accent>/g,
                        (_: any, text: any) => `<span style="color: #C45526; font-weight: 600;">${text}</span>`
                    )
                }}
            />
        </div>
    </>
}